import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Zap, Coins, Home, Play, Trophy, RotateCcw } from 'lucide-react';

const API_URL = 'https://ahantravyuh.com/plane/api';

// Game constants
const LANE_WIDTH = 2;
const LANES = [-LANE_WIDTH, 0, LANE_WIDTH];
const JUMP_FORCE = 0.4;
const GRAVITY = 0.02;
const INITIAL_GAME_SPEED = 0.15;
const SPEED_INCREMENT = 0.00002;

// Stage definitions - 10 stages, 500m each
// Aviation theme: From takeoff to space exploration
const STAGES = [
  {
    name: 'Runway',
    minDistance: 0,
    groundColor: 0x4a4a4a,    // Gray tarmac
    skyColor: 0x87ceeb,       // Light blue sky
    cloudColor: 0xffffff,     // White clouds
    fogColor: 0x87ceeb,
    collectible: 'fuel',
    collectibleColor: 0xff6600,
    collectibleEmissive: 0x331100,
    collectibleName: 'Fuel'
  },
  {
    name: 'Low Clouds',
    minDistance: 500,
    groundColor: 0xd0e8f0,    // Light cloudy
    skyColor: 0xadd8e6,       // Pale blue
    cloudColor: 0xffffff,     // Fluffy white
    fogColor: 0xc8e0e8,
    collectible: 'cloud',
    collectibleColor: 0xf0f8ff,
    collectibleEmissive: 0x444466,
    collectibleName: 'Cloud Puff'
  },
  {
    name: 'Sunset',
    minDistance: 1000,
    groundColor: 0xf4a460,    // Sandy gold below
    skyColor: 0xff7f50,       // Coral sunset
    cloudColor: 0xffd700,     // Golden clouds
    fogColor: 0xe8a060,
    collectible: 'feather',
    collectibleColor: 0xffd700,
    collectibleEmissive: 0x664400,
    collectibleName: 'Golden Feather'
  },
  {
    name: 'Ocean',
    minDistance: 1500,
    groundColor: 0x006994,    // Deep ocean blue
    skyColor: 0x5bc0de,       // Ocean sky
    cloudColor: 0xffffff,     // White clouds
    fogColor: 0x4a90c2,
    collectible: 'seabird',
    collectibleColor: 0xffffff,
    collectibleEmissive: 0x333333,
    collectibleName: 'Seabird'
  },
  {
    name: 'Thunderstorm',
    minDistance: 2000,
    groundColor: 0x2d2d4a,    // Dark storm clouds below
    skyColor: 0x1a1a2e,       // Dark stormy sky
    cloudColor: 0x4a4a6a,     // Dark clouds
    fogColor: 0x2a2a4a,
    collectible: 'lightning',
    collectibleColor: 0xffff00,
    collectibleEmissive: 0x444400,
    collectibleName: 'Lightning Bolt'
  },
  {
    name: 'Arctic',
    minDistance: 2500,
    groundColor: 0xe8f4f8,    // Snow white
    skyColor: 0xadd8e6,       // Icy blue
    cloudColor: 0xffffff,     // Pure white
    fogColor: 0xc8e0e8,
    collectible: 'snowflake',
    collectibleColor: 0xe0ffff,
    collectibleEmissive: 0x224444,
    collectibleName: 'Snowflake'
  },
  {
    name: 'Night Flight',
    minDistance: 3000,
    groundColor: 0x0a0a1a,    // Dark earth below
    skyColor: 0x0a0a2e,       // Dark night sky
    cloudColor: 0x2a2a4a,     // Dark wisps
    fogColor: 0x0a0a2a,
    collectible: 'star',
    collectibleColor: 0xfffacd,
    collectibleEmissive: 0x444422,
    collectibleName: 'Star'
  },
  {
    name: 'Aurora',
    minDistance: 3500,
    groundColor: 0x1a2a3a,    // Dark below
    skyColor: 0x2e1a4a,       // Purple-blue
    cloudColor: 0x00ff88,     // Green aurora
    fogColor: 0x2a3a4a,
    collectible: 'aurora',
    collectibleColor: 0x00ff88,
    collectibleEmissive: 0x004422,
    collectibleName: 'Aurora Wisp'
  },
  {
    name: 'Stratosphere',
    minDistance: 4000,
    groundColor: 0x000020,    // Near black below
    skyColor: 0x000044,       // Deep space blue
    cloudColor: 0x4466aa,     // Thin blue
    fogColor: 0x000033,
    collectible: 'oxygen',
    collectibleColor: 0x00bfff,
    collectibleEmissive: 0x003344,
    collectibleName: 'Oxygen Tank'
  },
  {
    name: 'Space',
    minDistance: 4500,
    groundColor: 0x000000,    // Pure black
    skyColor: 0x000011,       // Space black
    cloudColor: 0x222244,     // Cosmic dust
    fogColor: 0x000022,
    collectible: 'satellite',
    collectibleColor: 0xc0c0c0,
    collectibleEmissive: 0x333333,
    collectibleName: 'Satellite'
  }
];

// Stage stories - Aviation journey narrative
const STAGE_STORIES: { [key: string]: string } = {
  'Runway': "Engine roaring, your plane accelerates down the runway. The control tower clears you for takeoff. Pull back on the stick — you're leaving the ground behind. Your journey through the skies begins!",
  'Low Clouds': "Climbing through the first layer of clouds, visibility drops to zero. Trust your instruments. The fluffy white world around you is beautiful but deceptive. Stay focused, pilot!",
  'Sunset': "Breaking through the clouds into golden light! The sunset paints the sky in brilliant oranges and golds. Up here, above the world, you understand why pilots love to fly.",
  'Ocean': "The vast ocean stretches endlessly below — nothing but blue in every direction. Ships look like toys from this altitude. You're truly free up here, with the whole world beneath your wings.",
  'Thunderstorm': "Warning lights flash! A massive storm cell blocks your path. Lightning cracks around you, turbulence shakes the airframe. Only the brave fly through the storm — are you ready?",
  'Arctic': "The frozen north unfolds below — endless white glaciers and ice. The air is thin and cold. Your breath fogs the canopy as you navigate over the top of the world.",
  'Night Flight': "Stars appear as the sun sets behind you. Flying by starlight, cities glow like constellations below. The boundary between earth and sky blurs in the darkness.",
  'Aurora': "The Northern Lights dance around your aircraft! Green and purple curtains of light ripple across the sky. Few pilots ever witness this — you're among the lucky ones.",
  'Stratosphere': "The sky darkens to deep blue as you climb higher than commercial jets dare. The curvature of Earth becomes visible. You're reaching the edge of the atmosphere!",
  'Space': "You've done it! Breaking through the final barrier, you enter the void of space. Stars surround you, unblinking. Earth is a blue marble below. You've become a legend — a pilot among the stars!"
};

// Power-up definitions
const POWERUPS = {
  shield: { name: 'Shield', color: 0x00ffff, duration: 300, emoji: '🛡️' },
  magnet: { name: 'Magnet', color: 0xff00ff, duration: 300, emoji: '🧲' },
  double: { name: '2x Score', color: 0xffff00, duration: 600, emoji: '✨' }
};

// Achievement definitions
const ACHIEVEMENTS = [
  { id: 'first_flight', name: 'First Flight', desc: 'Complete your first flight', check: (s: any) => s.gamesPlayed >= 1 },
  { id: 'distance_1k', name: 'Solo Pilot', desc: 'Fly 1,000m', check: (s: any) => s.bestDistance >= 1000 },
  { id: 'distance_5k', name: 'Captain', desc: 'Fly 5,000m', check: (s: any) => s.bestDistance >= 5000 },
  { id: 'collect_100', name: 'Collector', desc: 'Collect 100 items', check: (s: any) => s.totalCollected >= 100 },
  { id: 'collect_500', name: 'Hoarder', desc: 'Collect 500 items', check: (s: any) => s.totalCollected >= 500 },
  { id: 'stage_5', name: 'Storm Chaser', desc: 'Reach Thunderstorm (Stage 5)', check: (s: any) => s.maxStage >= 5 },
  { id: 'stage_10', name: 'Astronaut', desc: 'Reach Space (Stage 10)', check: (s: any) => s.maxStage >= 10 },
  { id: 'powerup_10', name: 'Powered Up', desc: 'Collect 10 power-ups', check: (s: any) => s.powerupsCollected >= 10 },
  { id: 'games_10', name: 'Frequent Flyer', desc: 'Complete 10 flights', check: (s: any) => s.gamesPlayed >= 10 },
  { id: 'games_50', name: 'Ace Pilot', desc: 'Complete 50 flights', check: (s: any) => s.gamesPlayed >= 50 }
];

// Stage-specific obstacle types
const STAGE_OBSTACLES: { [key: string]: { type: string; color: number; height: number }[] } = {
  'Runway': [
    { type: 'crate', color: 0x8b4513, height: 0.8 },
    { type: 'cone', color: 0xff6600, height: 1.3 },
    { type: 'luggage', color: 0x4a4a4a, height: 0.9 }
  ],
  'Low Clouds': [
    { type: 'balloon', color: 0xff4444, height: 1.2 },
    { type: 'bird', color: 0x444444, height: 0.8 },
    { type: 'drone', color: 0x888888, height: 1.0 }
  ],
  'Sunset': [
    { type: 'hot_air', color: 0xff6b6b, height: 2.0 },
    { type: 'glider', color: 0xffffff, height: 1.2 },
    { type: 'bird_flock', color: 0x333333, height: 0.8 }
  ],
  'Ocean': [
    { type: 'seagull', color: 0xffffff, height: 0.8 },
    { type: 'ship_mast', color: 0x8b4513, height: 2.5 },
    { type: 'wave', color: 0x1e90ff, height: 1.5 }
  ],
  'Thunderstorm': [
    { type: 'lightning', color: 0xffff00, height: 3.0 },
    { type: 'dark_cloud', color: 0x2a2a4a, height: 1.8 },
    { type: 'hail', color: 0xaaaaaa, height: 0.6 }
  ],
  'Arctic': [
    { type: 'ice_chunk', color: 0xadd8e6, height: 1.0 },
    { type: 'iceberg', color: 0xe0ffff, height: 2.2 },
    { type: 'polar_wind', color: 0xffffff, height: 1.5 }
  ],
  'Night Flight': [
    { type: 'mountain', color: 0x2a2a2a, height: 2.5 },
    { type: 'bat', color: 0x1a1a1a, height: 0.7 },
    { type: 'tower', color: 0xff0000, height: 3.0 }
  ],
  'Aurora': [
    { type: 'ice_crystal', color: 0x00ff88, height: 1.2 },
    { type: 'frozen_cloud', color: 0x88ffcc, height: 1.8 },
    { type: 'solar_wind', color: 0xff00ff, height: 1.0 }
  ],
  'Stratosphere': [
    { type: 'debris', color: 0x666666, height: 0.9 },
    { type: 'weather_balloon', color: 0xffffff, height: 1.5 },
    { type: 'thin_cloud', color: 0x4466aa, height: 1.0 }
  ],
  'Space': [
    { type: 'asteroid', color: 0x555555, height: 1.5 },
    { type: 'satellite', color: 0xc0c0c0, height: 1.2 },
    { type: 'space_debris', color: 0x444444, height: 0.8 }
  ]
};

interface UserInfo {
  ahn_id: string;
  name: string;
  ahn_balance: number;
  urja_balance: number;
}

interface DailyStats {
  urja_earned: number;
  games_played: number;
  best_distance: number;
  urja_remaining: number;
  daily_cap: number;
}

interface LeaderboardEntry {
  rank: number;
  name?: string;
  distance: number;
}

type GameState = 'menu' | 'playing' | 'gameover';

function App() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [gameState, setGameState] = useState<GameState>('menu');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [distance, setDistance] = useState(0);
  const [gems, setGems] = useState(0);
  const [lastUrja, setLastUrja] = useState(0);
  const [isNewBest, setIsNewBest] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [currentStage, setCurrentStage] = useState(STAGES[0]);
  const [activePowerups, setActivePowerups] = useState<{ [key: string]: number }>({});
  const [showAchievement, setShowAchievement] = useState<string | null>(null);
  const [playerStats, setPlayerStats] = useState(() => {
    const saved = localStorage.getItem('plane_stats');
    return saved ? JSON.parse(saved) : {
      gamesPlayed: 0,
      bestDistance: 0,
      totalCollected: 0,
      maxStage: 1,
      powerupsCollected: 0,
      achievements: [] as string[]
    };
  });
  const [showStory, setShowStory] = useState(false);
  const [storyStage, setStoryStage] = useState<typeof STAGES[0] | null>(null);

  // Three.js refs
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const playerRef = useRef<THREE.Object3D | null>(null);
  const obstaclesRef = useRef<THREE.Mesh[]>([]);
  const gemsRef = useRef<THREE.Mesh[]>([]);
  const groundsRef = useRef<THREE.Mesh[]>([]);
  const cloudsRef = useRef<THREE.Mesh[]>([]);
  const stageIndexRef = useRef(0);
  const lastStoryStageRef = useRef(-1);
  const powerupsRef = useRef<THREE.Mesh[]>([]);

  // Power-up state refs
  const shieldActiveRef = useRef(false);
  const magnetActiveRef = useRef(false);
  const doubleActiveRef = useRef(false);
  const shieldTimerRef = useRef(0);
  const magnetTimerRef = useRef(0);
  const doubleTimerRef = useRef(0);

  // Game state refs
  const animationRef = useRef<number>(0);
  const gameStateRef = useRef<GameState>('menu');
  const gameRunningRef = useRef(true);
  const speedRef = useRef(INITIAL_GAME_SPEED);
  const distanceRef = useRef(0);
  const gemsRef2 = useRef(0);
  const laneRef = useRef(1);
  const velocityYRef = useRef(0);
  const jumpingRef = useRef(false);
  const frameRef = useRef(0);

  // Visual effects refs
  const trailMeshesRef = useRef<THREE.Mesh[]>([]);

  // Sync gameState to ref
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Save stats
  useEffect(() => {
    localStorage.setItem('plane_stats', JSON.stringify(playerStats));
  }, [playerStats]);

  // Fetch user data
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    if (token) {
      fetch(`${API_URL}/me`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => d.success && setUser(d.user))
        .catch(console.error);
      fetch(`${API_URL}/me/daily`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => d.success && setDailyStats(d.daily))
        .catch(console.error);
    }
    fetch(`${API_URL}/leaderboard`)
      .then(r => r.json())
      .then(d => d.success && setLeaderboard(d.leaderboard))
      .catch(console.error);
  }, []);

  // Get current stage based on distance
  const getCurrentStage = useCallback((dist: number) => {
    for (let i = STAGES.length - 1; i >= 0; i--) {
      if (dist >= STAGES[i].minDistance) return { stage: STAGES[i], index: i };
    }
    return { stage: STAGES[0], index: 0 };
  }, []);

  // Update stage visuals
  const updateStageVisuals = useCallback((stage: typeof STAGES[0]) => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    // Update background
    scene.background = new THREE.Color(stage.skyColor);
    scene.fog = new THREE.Fog(stage.fogColor, 20, 60);

    // Update ground colors
    groundsRef.current.forEach(g => {
      (g.material as THREE.MeshStandardMaterial).color.setHex(stage.groundColor);
    });

    // Update cloud colors
    cloudsRef.current.forEach(c => {
      (c.material as THREE.MeshStandardMaterial).color.setHex(stage.cloudColor);
    });
  }, []);

  // Initialize Three.js
  useEffect(() => {
    if (!containerRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(STAGES[0].skyColor);
    scene.fog = new THREE.Fog(STAGES[0].fogColor, 20, 60);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.set(0, 4, -8);
    camera.lookAt(0, 1, 5);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const sun = new THREE.DirectionalLight(0xffffff, 0.8);
    sun.position.set(5, 10, -5);
    scene.add(sun);

    // Ground
    const groundGeo = new THREE.PlaneGeometry(8, 20);
    const groundMat = new THREE.MeshStandardMaterial({ color: STAGES[0].groundColor });
    for (let i = 0; i < 5; i++) {
      const ground = new THREE.Mesh(groundGeo, groundMat.clone());
      ground.rotation.x = -Math.PI / 2;
      ground.position.set(0, 0, i * 20);
      scene.add(ground);
      groundsRef.current.push(ground);
    }

    // Clouds
    const cloudGeo = new THREE.SphereGeometry(2, 8, 8);
    for (let i = 0; i < 15; i++) {
      const cloudMat = new THREE.MeshStandardMaterial({
        color: STAGES[0].cloudColor,
        transparent: true,
        opacity: 0.7
      });
      const cloud = new THREE.Mesh(cloudGeo, cloudMat);
      cloud.position.set(
        (Math.random() - 0.5) * 20,
        8 + Math.random() * 5,
        Math.random() * 80
      );
      cloud.scale.set(1 + Math.random(), 0.5 + Math.random() * 0.5, 1 + Math.random());
      scene.add(cloud);
      cloudsRef.current.push(cloud);
    }

    // Load Plane model
    const loader = new GLTFLoader();
    loader.load(
      process.env.PUBLIC_URL + '/plane.glb',
      (gltf) => {
        const player = gltf.scene;
        player.scale.set(0.6, 0.6, 0.6);
        player.position.set(0, 1.5, 0);
        player.rotation.y = Math.PI / 2;
        scene.add(player);
        playerRef.current = player;
        renderer.render(scene, camera);
        setSceneReady(true);
      },
      undefined,
      (error) => {
        console.error('Error loading plane model:', error);
        // Fallback: Create simple plane shape
        const bodyGeo = new THREE.CylinderGeometry(0.2, 0.3, 2, 8);
        const bodyMat = new THREE.MeshStandardMaterial({ color: 0x0077cc, metalness: 0.7 });
        const body = new THREE.Mesh(bodyGeo, bodyMat);
        body.rotation.z = Math.PI / 2;

        const wingGeo = new THREE.BoxGeometry(3, 0.1, 0.6);
        const wingMat = new THREE.MeshStandardMaterial({ color: 0x0055aa });
        const wings = new THREE.Mesh(wingGeo, wingMat);

        const tailGeo = new THREE.BoxGeometry(0.8, 0.5, 0.1);
        const tail = new THREE.Mesh(tailGeo, wingMat);
        tail.position.set(-0.8, 0.2, 0);

        const plane = new THREE.Group();
        plane.add(body);
        plane.add(wings);
        plane.add(tail);
        plane.position.set(0, 1.5, 0);

        scene.add(plane);
        playerRef.current = plane;
        renderer.render(scene, camera);
        setSceneReady(true);
      }
    );

    // Handle resize
    const onResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
      renderer.render(scene, camera);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(animationRef.current);
      renderer.dispose();
      if (containerRef.current && renderer.domElement.parentNode === containerRef.current) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  // Game loop
  const gameLoop = useCallback(() => {
    if (gameStateRef.current !== 'playing' || !gameRunningRef.current) {
      animationRef.current = requestAnimationFrame(gameLoop);
      return;
    }
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !playerRef.current) return;

    frameRef.current++;
    const player = playerRef.current;
    const scene = sceneRef.current;

    // Speed up
    speedRef.current += SPEED_INCREMENT;
    distanceRef.current += speedRef.current * 10;

    // Check stage change
    const { stage, index } = getCurrentStage(distanceRef.current);
    if (index !== stageIndexRef.current) {
      stageIndexRef.current = index;
      setCurrentStage(stage);
      updateStageVisuals(stage);

      // Show story for new stage (only once per stage)
      if (index > lastStoryStageRef.current) {
        lastStoryStageRef.current = index;
        setStoryStage(stage);
        setShowStory(true);
        gameRunningRef.current = false;

        // Update max stage
        setPlayerStats((prev: any) => ({
          ...prev,
          maxStage: Math.max(prev.maxStage, index + 1)
        }));
      }
    }

    // Move player to lane
    const targetX = LANES[laneRef.current];
    player.position.x += (targetX - player.position.x) * 0.15;

    // Jump / altitude change
    if (jumpingRef.current) {
      velocityYRef.current -= GRAVITY;
      player.position.y += velocityYRef.current;
      if (player.position.y <= 1.5) {
        player.position.y = 1.5;
        jumpingRef.current = false;
        velocityYRef.current = 0;
      }
    }

    // Slight banking effect
    const targetBank = (targetX - player.position.x) * 0.3;
    player.rotation.z = THREE.MathUtils.lerp(player.rotation.z, targetBank, 0.1);

    // Move ground
    groundsRef.current.forEach(g => {
      g.position.z -= speedRef.current;
      if (g.position.z < -20) g.position.z += 100;
    });

    // Move clouds
    cloudsRef.current.forEach(c => {
      c.position.z -= speedRef.current * 0.5;
      if (c.position.z < -20) {
        c.position.z = 60 + Math.random() * 20;
        c.position.x = (Math.random() - 0.5) * 20;
      }
    });

    // Update power-up timers
    if (shieldActiveRef.current) {
      shieldTimerRef.current--;
      if (shieldTimerRef.current <= 0) {
        shieldActiveRef.current = false;
        setActivePowerups(p => { const n = {...p}; delete n.shield; return n; });
      }
    }
    if (magnetActiveRef.current) {
      magnetTimerRef.current--;
      if (magnetTimerRef.current <= 0) {
        magnetActiveRef.current = false;
        setActivePowerups(p => { const n = {...p}; delete n.magnet; return n; });
      }
    }
    if (doubleActiveRef.current) {
      doubleTimerRef.current--;
      if (doubleTimerRef.current <= 0) {
        doubleActiveRef.current = false;
        setActivePowerups(p => { const n = {...p}; delete n.double; return n; });
      }
    }

    // Move obstacles & check collision
    for (let i = obstaclesRef.current.length - 1; i >= 0; i--) {
      const obs = obstaclesRef.current[i];
      obs.position.z -= speedRef.current;

      const dx = Math.abs(obs.position.x - player.position.x);
      const dz = Math.abs(obs.position.z - player.position.z);
      const h = obs.userData.height || 1;

      if (dx < 0.8 && dz < 0.8 && player.position.y < h + 0.8) {
        if (shieldActiveRef.current) {
          // Shield protects
          scene.remove(obs);
          obstaclesRef.current.splice(i, 1);
        } else {
          gameOver();
          return;
        }
      }

      if (obs.position.z < -10) {
        scene.remove(obs);
        obstaclesRef.current.splice(i, 1);
      }
    }

    // Move gems & check collection
    for (let i = gemsRef.current.length - 1; i >= 0; i--) {
      const gem = gemsRef.current[i];
      gem.position.z -= speedRef.current;
      gem.rotation.y += 0.05;

      // Magnet effect
      if (magnetActiveRef.current) {
        const toPlayer = new THREE.Vector3(
          player.position.x - gem.position.x,
          player.position.y - gem.position.y,
          player.position.z - gem.position.z
        );
        if (toPlayer.length() < 5) {
          gem.position.add(toPlayer.normalize().multiplyScalar(0.3));
        }
      }

      const dx = Math.abs(gem.position.x - player.position.x);
      const dz = Math.abs(gem.position.z - player.position.z);
      const dy = Math.abs(gem.position.y - player.position.y);

      if (dx < 1.2 && dz < 1.2 && dy < 1.5) {
        const value = gem.userData.value * (doubleActiveRef.current ? 2 : 1);
        gemsRef2.current += value;
        scene.remove(gem);
        gemsRef.current.splice(i, 1);
        continue;
      }

      if (gem.position.z < -10) {
        scene.remove(gem);
        gemsRef.current.splice(i, 1);
      }
    }

    // Move power-ups & check collection
    for (let i = powerupsRef.current.length - 1; i >= 0; i--) {
      const pu = powerupsRef.current[i];
      pu.position.z -= speedRef.current;
      pu.rotation.y += 0.08;
      pu.rotation.x += 0.02;

      const dx = Math.abs(pu.position.x - player.position.x);
      const dz = Math.abs(pu.position.z - player.position.z);

      if (dx < 1.2 && dz < 1.2) {
        const type = pu.userData.type;
        const config = POWERUPS[type as keyof typeof POWERUPS];

        if (type === 'shield') {
          shieldActiveRef.current = true;
          shieldTimerRef.current = config.duration;
        } else if (type === 'magnet') {
          magnetActiveRef.current = true;
          magnetTimerRef.current = config.duration;
        } else if (type === 'double') {
          doubleActiveRef.current = true;
          doubleTimerRef.current = config.duration;
        }

        setActivePowerups(p => ({ ...p, [type]: config.duration }));
        setPlayerStats((prev: any) => ({ ...prev, powerupsCollected: prev.powerupsCollected + 1 }));

        scene.remove(pu);
        powerupsRef.current.splice(i, 1);
        continue;
      }

      if (pu.position.z < -10) {
        scene.remove(pu);
        powerupsRef.current.splice(i, 1);
      }
    }

    // Get current stage obstacles
    const stageObs = STAGE_OBSTACLES[stage.name] || STAGE_OBSTACLES['Runway'];

    // Spawn obstacles
    if (frameRef.current % 60 === 0) {
      const lane = Math.floor(Math.random() * 3);
      const obsType = stageObs[Math.floor(Math.random() * stageObs.length)];

      let geo;
      if (obsType.height < 1) {
        geo = new THREE.BoxGeometry(1, obsType.height, 1);
      } else if (obsType.height < 2) {
        geo = new THREE.CylinderGeometry(0.4, 0.5, obsType.height, 8);
      } else {
        geo = new THREE.BoxGeometry(0.8, obsType.height, 0.8);
      }

      const obs = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: obsType.color }));
      obs.position.set(LANES[lane], obsType.height / 2, 50);
      obs.userData.height = obsType.height;
      scene.add(obs);
      obstaclesRef.current.push(obs);
    }

    // Spawn collectibles
    if (frameRef.current % 35 === 0) {
      const lane = Math.floor(Math.random() * 3);
      const rare = Math.random() < 0.1;
      const gem = new THREE.Mesh(
        new THREE.SphereGeometry(0.3, 16, 16),
        new THREE.MeshStandardMaterial({
          color: rare ? 0x00ff88 : stage.collectibleColor,
          emissive: rare ? 0x004422 : stage.collectibleEmissive
        })
      );
      gem.position.set(LANES[lane], 1.8, 50);
      gem.userData.value = rare ? 50 : 10;
      scene.add(gem);
      gemsRef.current.push(gem);
    }

    // Spawn power-ups (rare)
    if (frameRef.current % 300 === 0 && Math.random() < 0.5) {
      const lane = Math.floor(Math.random() * 3);
      const types = Object.keys(POWERUPS);
      const type = types[Math.floor(Math.random() * types.length)];
      const config = POWERUPS[type as keyof typeof POWERUPS];

      const pu = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.5),
        new THREE.MeshStandardMaterial({ color: config.color, emissive: config.color, emissiveIntensity: 0.3 })
      );
      pu.position.set(LANES[lane], 2.5, 50);
      pu.userData.type = type;
      scene.add(pu);
      powerupsRef.current.push(pu);
    }

    // Update UI
    if (frameRef.current % 10 === 0) {
      setDistance(Math.floor(distanceRef.current));
      setGems(gemsRef2.current);
    }

    rendererRef.current.render(sceneRef.current, cameraRef.current);
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [getCurrentStage, updateStageVisuals]);

  // Game over
  const gameOver = useCallback(async () => {
    setGameState('gameover');
    cancelAnimationFrame(animationRef.current);

    // Update stats
    setPlayerStats((prev: any) => {
      const newStats = {
        ...prev,
        gamesPlayed: prev.gamesPlayed + 1,
        bestDistance: Math.max(prev.bestDistance, Math.floor(distanceRef.current)),
        totalCollected: prev.totalCollected + gemsRef2.current
      };

      // Check achievements
      ACHIEVEMENTS.forEach(ach => {
        if (!newStats.achievements.includes(ach.id) && ach.check(newStats)) {
          newStats.achievements.push(ach.id);
          setShowAchievement(ach.name);
          setTimeout(() => setShowAchievement(null), 3000);
        }
      });

      return newStats;
    });

    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await fetch(`${API_URL}/game/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ distance: Math.floor(distanceRef.current), gems_collected: gemsRef2.current })
        });
        const data = await res.json();
        if (data.success) {
          setLastUrja(data.urja_earned);
          setIsNewBest(data.is_new_best);
          fetch(`${API_URL}/me`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => d.success && setUser(d.user));
          fetch(`${API_URL}/me/daily`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => d.success && setDailyStats(d.daily));
        }
      } catch (e) {
        console.error(e);
      }
    }
    fetch(`${API_URL}/leaderboard`).then(r => r.json()).then(d => d.success && setLeaderboard(d.leaderboard));
  }, []);

  // Start game
  const startGame = useCallback(() => {
    if (!sceneRef.current || !playerRef.current) {
      console.error('Scene not ready');
      return;
    }

    // Reset
    speedRef.current = INITIAL_GAME_SPEED;
    distanceRef.current = 0;
    gemsRef2.current = 0;
    laneRef.current = 1;
    velocityYRef.current = 0;
    jumpingRef.current = false;
    frameRef.current = 0;
    stageIndexRef.current = 0;
    lastStoryStageRef.current = -1;
    gameRunningRef.current = true;
    setDistance(0);
    setGems(0);
    setLastUrja(0);
    setIsNewBest(false);
    setCurrentStage(STAGES[0]);
    setActivePowerups({});

    // Reset power-ups
    shieldActiveRef.current = false;
    magnetActiveRef.current = false;
    doubleActiveRef.current = false;

    // Clear objects
    obstaclesRef.current.forEach(o => sceneRef.current?.remove(o));
    gemsRef.current.forEach(g => sceneRef.current?.remove(g));
    powerupsRef.current.forEach(p => sceneRef.current?.remove(p));
    obstaclesRef.current = [];
    gemsRef.current = [];
    powerupsRef.current = [];

    // Reset visuals
    updateStageVisuals(STAGES[0]);

    // Reset player
    playerRef.current.position.set(0, 1.5, 0);
    playerRef.current.rotation.z = 0;

    setGameState('playing');
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [gameLoop, updateStageVisuals]);

  // Continue from story
  const continueFromStory = useCallback(() => {
    setShowStory(false);
    gameRunningRef.current = true;
  }, []);

  // Controls
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (showStory && (e.key === ' ' || e.key === 'Enter')) {
        continueFromStory();
        return;
      }
      if (gameStateRef.current !== 'playing') return;
      if (e.key === 'ArrowLeft' || e.key === 'a') laneRef.current = Math.max(0, laneRef.current - 1);
      if (e.key === 'ArrowRight' || e.key === 'd') laneRef.current = Math.min(2, laneRef.current + 1);
      if ((e.key === 'ArrowUp' || e.key === 'w' || e.key === ' ') && !jumpingRef.current) {
        jumpingRef.current = true;
        velocityYRef.current = JUMP_FORCE;
      }
    };

    let touchX = 0, touchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (showStory) {
        continueFromStory();
        return;
      }
      if (gameStateRef.current !== 'playing') return;
      const dx = e.changedTouches[0].clientX - touchX;
      const dy = e.changedTouches[0].clientY - touchY;
      if (Math.abs(dx) > Math.abs(dy)) {
        if (dx > 30) laneRef.current = Math.min(2, laneRef.current + 1);
        if (dx < -30) laneRef.current = Math.max(0, laneRef.current - 1);
      } else if (dy < -30 && !jumpingRef.current) {
        jumpingRef.current = true;
        velocityYRef.current = JUMP_FORCE;
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [showStory, continueFromStory]);

  return (
    <div className="h-screen bg-slate-900 text-slate-100 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-sky-500 rounded-full flex items-center justify-center">✈️</div>
            <div>
              <h1 className="text-xl font-bold text-sky-400">PLANE</h1>
              <p className="text-xs text-slate-400">Fly High. Explore. Conquer.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <>
                <div className="bg-slate-700 px-2 py-1 rounded text-sm hidden sm:block">
                  <Coins className="w-4 h-4 text-yellow-400 inline mr-1" />{user.ahn_balance.toFixed(0)}
                </div>
                <div className="bg-slate-700 px-2 py-1 rounded text-sm hidden sm:block">
                  <Zap className="w-4 h-4 text-purple-400 inline mr-1" />{user.urja_balance.toFixed(0)}
                </div>
              </>
            )}
            <button onClick={() => setShowLeaderboard(true)} className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg">
              <Trophy className="w-5 h-5" />
            </button>
            <button onClick={() => window.location.href = '/'} className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg">
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Game */}
      <div className="flex-1 relative">
        <div ref={containerRef} className="w-full h-full" />

        {/* Stage indicator */}
        {gameState === 'playing' && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/60 px-4 py-1 rounded-full">
            <span className="text-sm text-sky-300">{currentStage.name}</span>
          </div>
        )}

        {/* Active power-ups */}
        {gameState === 'playing' && Object.keys(activePowerups).length > 0 && (
          <div className="absolute top-14 left-1/2 transform -translate-x-1/2 flex gap-2">
            {Object.entries(activePowerups).map(([type, timer]) => (
              <div key={type} className="bg-black/60 px-2 py-1 rounded text-sm">
                {POWERUPS[type as keyof typeof POWERUPS].emoji}
              </div>
            ))}
          </div>
        )}

        {/* HUD */}
        {gameState === 'playing' && (
          <div className="absolute top-4 left-4 right-4 flex justify-between pointer-events-none">
            <div className="bg-black/50 px-4 py-2 rounded-lg">
              <div className="text-2xl font-bold text-sky-400">{distance}m</div>
            </div>
            <div className="bg-black/50 px-4 py-2 rounded-lg">
              <span className="text-yellow-400 text-xl">+{gems}</span>
            </div>
          </div>
        )}

        {/* Menu */}
        {gameState === 'menu' && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <div className="text-center p-8">
              <div className="w-20 h-20 bg-sky-600 rounded-full flex items-center justify-center mx-auto mb-4 text-4xl">
                ✈️
              </div>
              <h2 className="text-3xl font-bold text-sky-400 mb-2">PLANE</h2>
              <p className="text-slate-400 mb-6">Fly through the skies, reach space, earn URJA!</p>

              {isLoggedIn ? (
                <>
                  <button
                    onClick={startGame}
                    disabled={!sceneReady}
                    className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-slate-600 text-white text-xl font-bold py-4 px-8 rounded-xl mb-4"
                  >
                    {sceneReady ? 'START FLIGHT' : 'Loading...'}
                  </button>
                  {dailyStats && (
                    <p className="text-sm text-slate-400">Today: {dailyStats.urja_earned}/{dailyStats.daily_cap} URJA</p>
                  )}
                  <p className="text-xs text-slate-500 mt-2">Best: {playerStats.bestDistance}m | Stage: {playerStats.maxStage}/10</p>
                </>
              ) : (
                <button onClick={() => window.location.href = '/'} className="bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg">
                  Login to Play
                </button>
              )}

              <p className="mt-4 text-xs text-slate-500">Controls: ← → to steer | ↑ / Space to climb</p>
            </div>
          </div>
        )}

        {/* Story Modal */}
        {showStory && storyStage && (
          <div className="absolute inset-0 bg-black/90 flex items-center justify-center p-4 z-50">
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl border border-sky-500/30 p-6 max-w-md text-center">
              <div className="text-4xl mb-4">
                {storyStage.name === 'Runway' && '🛫'}
                {storyStage.name === 'Low Clouds' && '☁️'}
                {storyStage.name === 'Sunset' && '🌅'}
                {storyStage.name === 'Ocean' && '🌊'}
                {storyStage.name === 'Thunderstorm' && '⛈️'}
                {storyStage.name === 'Arctic' && '❄️'}
                {storyStage.name === 'Night Flight' && '🌙'}
                {storyStage.name === 'Aurora' && '🌌'}
                {storyStage.name === 'Stratosphere' && '🚀'}
                {storyStage.name === 'Space' && '🛸'}
              </div>
              <h3 className="text-2xl font-bold text-sky-400 mb-4">{storyStage.name}</h3>
              <p className="text-slate-300 leading-relaxed mb-6">{STAGE_STORIES[storyStage.name]}</p>
              <button
                onClick={continueFromStory}
                className="bg-sky-600 hover:bg-sky-700 px-8 py-3 rounded-xl font-bold"
              >
                Continue Flight →
              </button>
              <p className="text-xs text-slate-500 mt-3">Press Space or tap to continue</p>
            </div>
          </div>
        )}

        {/* Achievement popup */}
        {showAchievement && (
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-yellow-600 px-6 py-3 rounded-xl animate-bounce z-40">
            <div className="text-center">
              <div className="text-xl">🏆</div>
              <div className="font-bold">{showAchievement}</div>
            </div>
          </div>
        )}

        {/* Game Over */}
        {gameState === 'gameover' && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
            <div className="text-center p-8 bg-slate-800 rounded-2xl border border-slate-700 max-w-sm">
              <h2 className="text-3xl font-bold text-red-400 mb-4">FLIGHT ENDED</h2>

              {isNewBest && (
                <div className="bg-yellow-600/20 border border-yellow-600 text-yellow-400 px-4 py-2 rounded-lg mb-4">
                  ✨ NEW RECORD!
                </div>
              )}

              <div className="bg-slate-700/50 rounded-lg p-2 mb-4">
                <span className="text-sky-300">Stage: {currentStage.name}</span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="text-3xl font-bold text-sky-400">{distance}m</div>
                  <div className="text-xs text-slate-400">Distance</div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3">
                  <div className="text-3xl font-bold text-yellow-400">{gems}</div>
                  <div className="text-xs text-slate-400">Collected</div>
                </div>
              </div>

              {lastUrja > 0 && (
                <div className="bg-purple-600/20 border border-purple-600 rounded-lg p-3 mb-4">
                  <Zap className="w-5 h-5 text-purple-400 inline mr-2" />
                  <span className="text-xl font-bold text-purple-400">+{lastUrja} URJA</span>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={startGame} className="flex-1 bg-sky-600 hover:bg-sky-700 py-3 rounded-xl flex items-center justify-center gap-2">
                  <RotateCcw className="w-5 h-5" /> Fly Again
                </button>
                <button onClick={() => setGameState('menu')} className="bg-slate-700 hover:bg-slate-600 px-4 py-3 rounded-xl">
                  Menu
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {showLeaderboard && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 max-w-sm w-full max-h-[80vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
                  <Trophy className="w-5 h-5" /> Flight Records
                </h2>
                <button onClick={() => setShowLeaderboard(false)} className="text-slate-400 hover:text-white text-2xl">&times;</button>
              </div>
              {leaderboard.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No flights recorded yet!</p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.slice(0, 15).map((e, i) => (
                    <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${i < 3 ? 'bg-yellow-600/20' : 'bg-slate-700/50'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-yellow-500 text-black' : i === 1 ? 'bg-slate-400 text-black' : i === 2 ? 'bg-orange-600' : 'bg-slate-600'}`}>
                          {e.rank}
                        </span>
                        <span>{e.name || 'Pilot'}</span>
                      </div>
                      <span className="text-sky-400 font-bold">{e.distance}m</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
